<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import { api, getToken, setToken, reportValidationError } from './api'
import { createI18n, LocalePicker, M6_PICKER_LOCALES } from '@mybonet/i18n'
import { getLegalDoc, type LegalDocId } from './legalContent'

const ThemeExamples = defineAsyncComponent(() => import('./ThemeExamples.vue'))

type Approximate = { currency: string; amount: number; fxRate: number; note: string }
type Plan = {
  key: string
  name: string
  tagline: string
  monthlyUsd: number
  yearlyUsd: number
  maxChildSites: number
  features: string[]
  monthlyDisplay?: Approximate
  yearlyDisplay?: Approximate
}
type Pricing = {
  currency: string
  note: string
  plans: Plan[]
  addOns: {
    key: string
    name: string
    monthlyUsd: number
    yearlyUsd: number
    note: string
    monthlyDisplay?: Approximate
  }[]
  display: {
    detectedCountry?: string | null
    displayCurrency: string
    usdToDisplayRate: number
    note: string
    approximateOnly: boolean
  }
}
type Auth = {
  accessToken: string
  userId: string
  email: string
  displayName?: string
  maxChildSites: number
}

type Route =
  | 'home' | 'features' | 'pricing' | 'docs' | 'contact' | 'examples' | 'product'
  | 'privacy' | 'terms' | 'deletion'
  | 'login' | 'register' | 'forgot' | 'verify' | 'oauth-callback'

const i18n = createI18n({ enabled: [...M6_PICKER_LOCALES] })
const t = (key: string) => i18n.t(key)
const copyrightYear = new Date().getFullYear()

const logoUrl = `${import.meta.env.BASE_URL}assets/images/logo.png`
const memberConsoleUrl = import.meta.env.VITE_MEMBER_CONSOLE_URL
  || (import.meta.env.PROD ? 'https://mybo.net/console/' : 'http://127.0.0.1:5174/')
const route = ref<Route>('home')
const legalDoc = computed(() => {
  const id = route.value as LegalDocId
  if (id !== 'privacy' && id !== 'terms' && id !== 'deletion') return null
  return getLegalDoc(id, i18n.locale.value)
})
const pricing = ref<Pricing | null>(null)
const me = ref<Auth | null>(null)
const error = ref('')
const busy = ref(false)
const menuOpen = ref(false)
const form = reactive({
  email: 'demo@mybo.net',
  password: 'Demo1234!',
  displayName: 'Demo',
  code: '',
})
const regCodeCooldown = ref(0)
let regCodeTimer: ReturnType<typeof setInterval> | null = null
const regMsg = ref('')

const contact = reactive({
  name: '',
  email: '',
  code: '',
  subject: '',
  message: '',
})
const contactSendBusy = ref(false)
const contactSubmitBusy = ref(false)
const codeCooldown = ref(0)
let codeTimer: ReturnType<typeof setInterval> | null = null

type ToastKind = 'ok' | 'error'
type ToastItem = { id: number; kind: ToastKind; text: string }
const toasts = ref<ToastItem[]>([])
let toastSeq = 0
const toastTimers = new Map<number, ReturnType<typeof setTimeout>>()

function pushToast(kind: ToastKind, text: string, ms = 4200) {
  const id = ++toastSeq
  toasts.value = [...toasts.value, { id, kind, text }]
  const timer = setTimeout(() => dismissToast(id), ms)
  toastTimers.set(id, timer)
}

function dismissToast(id: number) {
  const timer = toastTimers.get(id)
  if (timer) {
    clearTimeout(timer)
    toastTimers.delete(id)
  }
  toasts.value = toasts.value.filter((x) => x.id !== id)
}

function mapContactError(raw: string): string {
  if (raw.startsWith('contact_verify_rate_limited')) return t('contact.err.rate')
  if (raw === 'contact_email_invalid') return t('contact.err.email')
  if (raw === 'contact_code_required' || raw === 'contact_code_invalid') return t('contact.err.code')
  if (raw === 'contact_message_invalid') return t('contact.err.message')
  if (raw === 'contact_name_required' || raw === 'contact_name_invalid') return t('contact.err.name')
  return raw
}

function startCooldown(seconds: number) {
  codeCooldown.value = Math.max(0, seconds)
  if (codeTimer) clearInterval(codeTimer)
  codeTimer = setInterval(() => {
    if (codeCooldown.value <= 1) {
      codeCooldown.value = 0
      if (codeTimer) clearInterval(codeTimer)
      codeTimer = null
      return
    }
    codeCooldown.value -= 1
  }, 1000)
}

async function sendContactCode() {
  if (contactSendBusy.value || contactSubmitBusy.value) return
  if (!contact.email.trim() || !contact.email.includes('@')) {
    pushToast('error', t('contact.err.email'))
    reportValidationError('contact_email_invalid')
    return
  }
  if (codeCooldown.value > 0) {
    pushToast('error', t('contact.err.rate'))
    return
  }
  contactSendBusy.value = true
  try {
    const res = await api<{ expiresAt: string; retryAfterSeconds: number; debugCode?: string | null }>(
      '/api/v2/platform/contact/verify-email',
      { method: 'POST', body: JSON.stringify({ email: contact.email.trim() }) },
    )
    pushToast('ok', t('contact.codeSent'))
    startCooldown(res.retryAfterSeconds || 60)
    if (res.debugCode) contact.code = res.debugCode
  } catch (e) {
    pushToast('error', mapContactError(e instanceof Error ? e.message : 'contact_failed'))
  } finally {
    contactSendBusy.value = false
  }
}

async function submitContact() {
  if (contactSendBusy.value || contactSubmitBusy.value) return
  if (!contact.name.trim()) {
    pushToast('error', t('contact.err.name'))
    reportValidationError('contact_name_required')
    return
  }
  if (!contact.email.trim() || !contact.email.includes('@')) {
    pushToast('error', t('contact.err.email'))
    reportValidationError('contact_email_invalid')
    return
  }
  if (!contact.code.trim()) {
    pushToast('error', t('contact.err.code'))
    reportValidationError('contact_code_required')
    return
  }
  if (contact.message.trim().length < 10) {
    pushToast('error', t('contact.err.message'))
    reportValidationError('contact_message_invalid')
    return
  }
  contactSubmitBusy.value = true
  try {
    await api('/api/v2/platform/contact', {
      method: 'POST',
      body: JSON.stringify({
        name: contact.name.trim(),
        email: contact.email.trim(),
        code: contact.code.trim(),
        subject: contact.subject.trim() || undefined,
        message: contact.message.trim(),
      }),
    })
    pushToast('ok', t('contact.success'))
    contact.code = ''
    contact.message = ''
    contact.subject = ''
  } catch (e) {
    pushToast('error', mapContactError(e instanceof Error ? e.message : 'contact_failed'))
  } finally {
    contactSubmitBusy.value = false
  }
}

const isAuthPage = computed(() =>
  ['login', 'register', 'forgot', 'verify'].includes(route.value),
)

const navLinks: { route: Route; key: string }[] = [
  { route: 'home', key: 'nav.home' },
  { route: 'features', key: 'nav.features' },
  { route: 'examples', key: 'nav.examples' },
  { route: 'pricing', key: 'nav.pricing' },
  { route: 'docs', key: 'nav.docs' },
  { route: 'contact', key: 'nav.contact' },
]

function hashQuery(): URLSearchParams {
  const raw = window.location.hash.replace(/^#\/?/, '') || ''
  const q = raw.indexOf('?')
  return new URLSearchParams(q >= 0 ? raw.slice(q + 1) : '')
}

/** Survive OAuth round-trips where `#/oauth-callback` drops login `?next=`. */
const AUTH_NEXT_KEY = 'mybo_auth_next'

function stashLoginNext(raw?: string | null) {
  const v = (raw ?? hashQuery().get('next') ?? '').trim()
  if (!v || !isSafeConsoleNext(v)) return
  try {
    sessionStorage.setItem(AUTH_NEXT_KEY, v)
  } catch {
    /* private mode / quota */
  }
}

function takeLoginNext(): string {
  const fromHash = (hashQuery().get('next') || '').trim()
  if (fromHash && isSafeConsoleNext(fromHash)) {
    try {
      sessionStorage.removeItem(AUTH_NEXT_KEY)
    } catch {
      /* ignore */
    }
    return fromHash
  }
  try {
    const stored = (sessionStorage.getItem(AUTH_NEXT_KEY) || '').trim()
    if (stored && isSafeConsoleNext(stored)) {
      sessionStorage.removeItem(AUTH_NEXT_KEY)
      return stored
    }
  } catch {
    /* ignore */
  }
  return ''
}

function parseHash(): Route {
  const raw = (window.location.hash.replace(/^#\/?/, '') || 'home').split('?')[0]
  const allowed: Route[] = [
    'home', 'features', 'examples', 'pricing', 'docs', 'contact', 'product',
    'privacy', 'terms', 'deletion',
    'login', 'register', 'forgot', 'verify', 'oauth-callback',
  ]
  if (raw === '' || raw === '/') return 'home'
  return (allowed.includes(raw as Route) ? raw : 'home') as Route
}

function closeMenu() {
  menuOpen.value = false
  document.body.classList.remove('nav-drawer-open')
}

function toggleMenu() {
  menuOpen.value = !menuOpen.value
  document.body.classList.toggle('nav-drawer-open', menuOpen.value)
}

function go(r: Route) {
  closeMenu()
  window.location.hash = r === 'home' ? '#/' : `#/${r}`
}

function scrollToTop() {
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  document.documentElement.scrollTop = 0
  document.body.scrollTop = 0
}

function onHash() {
  const next = parseHash()
  route.value = next
  closeMenu()
  scrollToTop()
  if (next === 'oauth-callback') {
    void finishOAuthCallback()
    return
  }
  if (next === 'login') {
    const err = hashQuery().get('error')
    if (err) error.value = err
    const n = (hashQuery().get('next') || '').trim()
    if (n) stashLoginNext(n)
    else {
      try {
        sessionStorage.removeItem(AUTH_NEXT_KEY)
      } catch {
        /* ignore */
      }
    }
  }
}

async function loadPricing() {
  try {
    const params = new URLSearchParams(window.location.search)
    const country = params.get('country')
    const q = country ? `?country=${encodeURIComponent(country)}` : ''
    pricing.value = await api<Pricing>(`/api/v2/platform/pricing${q}`)
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'pricing_failed'
  }
}

async function refreshMe() {
  const token = getToken()
  if (!token) {
    me.value = null
    return
  }
  try {
    const profile = await api<{ userId: string; email: string; displayName?: string; maxChildSites: number }>('/api/v2/auth/me')
    me.value = { accessToken: token, ...profile }
  } catch {
    setToken(null)
    me.value = null
  }
}

onMounted(() => {
  window.addEventListener('hashchange', onHash)
  onHash()
  // Do not block first paint / LCP on pricing API (home does not need it).
  void refreshMe()
  if (route.value === 'pricing') void loadPricing()
})
onUnmounted(() => {
  window.removeEventListener('hashchange', onHash)
  document.body.classList.remove('nav-drawer-open')
  if (codeTimer) clearInterval(codeTimer)
  if (regCodeTimer) clearInterval(regCodeTimer)
  for (const timer of toastTimers.values()) clearTimeout(timer)
  toastTimers.clear()
})

watch(route, (r) => {
  if (r === 'pricing' && !pricing.value) void loadPricing()
})

function startRegCooldown(seconds: number) {
  regCodeCooldown.value = Math.max(0, seconds)
  if (regCodeTimer) clearInterval(regCodeTimer)
  regCodeTimer = setInterval(() => {
    if (regCodeCooldown.value <= 1) {
      regCodeCooldown.value = 0
      if (regCodeTimer) clearInterval(regCodeTimer)
      regCodeTimer = null
      return
    }
    regCodeCooldown.value -= 1
  }, 1000)
}

function mapRegisterError(raw: string): string {
  if (raw.startsWith('register_verify_rate_limited')) return t('auth.err.rate')
  if (raw === 'register_email_invalid') return t('auth.err.email')
  if (raw === 'register_code_required' || raw === 'register_code_invalid') return t('auth.err.code')
  if (raw === 'email_already_registered') return t('auth.err.registered')
  if (raw === 'password_too_short') return t('auth.err.password')
  if (raw.startsWith('register_email_send_failed') || raw === 'email_adapter_missing') return t('auth.err.send')
  return raw
}

async function sendRegisterCode() {
  error.value = ''
  regMsg.value = ''
  if (!form.email.trim() || !form.email.includes('@')) {
    error.value = t('auth.err.email')
    reportValidationError('register_email_invalid')
    return
  }
  if (regCodeCooldown.value > 0) {
    error.value = t('auth.err.rate')
    return
  }
  busy.value = true
  try {
    const res = await api<{ expiresAt: string; retryAfterSeconds: number; debugCode?: string | null }>(
      '/api/v2/auth/register/verify-email',
      { method: 'POST', body: JSON.stringify({ email: form.email.trim() }) },
    )
    regMsg.value = t('auth.codeSent')
    startRegCooldown(res.retryAfterSeconds || 60)
    if (res.debugCode) form.code = res.debugCode
  } catch (e) {
    error.value = mapRegisterError(e instanceof Error ? e.message : 'register_failed')
  } finally {
    busy.value = false
  }
}

async function submitAuth(mode: 'login' | 'register') {
  error.value = ''
  regMsg.value = ''
  if (!form.email.trim() || !form.password.trim()) {
    error.value = 'email_password_required'
    reportValidationError('email_password_required', { mode })
    return
  }
  if (mode === 'register' && form.password.length < 6) {
    error.value = t('auth.err.password')
    reportValidationError('password_too_short', { mode })
    return
  }
  if (mode === 'register' && !form.code.trim()) {
    error.value = t('auth.err.code')
    reportValidationError('register_code_required', { mode })
    return
  }
  busy.value = true
  try {
    const path = mode === 'register' ? '/api/v2/auth/register' : '/api/v2/auth/login'
    const body = mode === 'register'
      ? { email: form.email, password: form.password, displayName: form.displayName, code: form.code.trim() }
      : { email: form.email, password: form.password }
    const auth = await api<Auth>(path, { method: 'POST', body: JSON.stringify(body) })
    setToken(auth.accessToken)
    me.value = auth
    goAdmin()
  } catch (e) {
    error.value = mode === 'register'
      ? mapRegisterError(e instanceof Error ? e.message : 'auth_failed')
      : (e instanceof Error ? e.message : 'auth_failed')
  } finally {
    busy.value = false
  }
}

async function finishOAuthCallback() {
  error.value = ''
  const token = hashQuery().get('accessToken')
  if (!token) {
    error.value = hashQuery().get('error') || 'oauth_failed'
    go('login')
    return
  }
  setToken(token)
  try {
    await refreshMe()
    goAdmin()
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'oauth_failed'
    go('login')
  }
}

async function oauth(provider: string) {
  error.value = ''
  stashLoginNext()
  // Real browser OAuth for Facebook / Google; LINE stays on demo until configured.
  if (provider === 'line') {
    try {
      const auth = await api<Auth>(`/api/v2/auth/oauth/${provider}/demo`, {
        method: 'POST',
        body: JSON.stringify({ displayName: `${provider} user` }),
      })
      setToken(auth.accessToken)
      me.value = auth
      goAdmin()
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'oauth_failed'
    }
    return
  }

  busy.value = true
  try {
    const nextRaw = takeLoginNext()
    // Re-stash: takeLoginNext clears storage; keep for post-callback goAdmin().
    if (nextRaw) stashLoginNext(nextRaw)
    const redirectUri = nextRaw && isSafeConsoleNext(nextRaw)
      ? `${window.location.origin}/#/oauth-callback?next=${encodeURIComponent(nextRaw)}`
      : `${window.location.origin}/#/oauth-callback`
    const started = await api<{ authorizeUrl: string }>('/api/v2/auth/social/start', {
      method: 'POST',
      body: JSON.stringify({
        provider,
        purpose: 'login',
        redirectUri,
      }),
    })
    window.location.href = started.authorizeUrl
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'oauth_failed'
    busy.value = false
  }
}

function isSafeConsoleNext(raw: string): boolean {
  try {
    const u = new URL(raw)
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return false
    const allowed = new Set<string>()
    for (const base of [memberConsoleUrl, import.meta.env.VITE_HQ_URL || '', window.location.origin]) {
      if (!base) continue
      try { allowed.add(new URL(base).host) } catch { /* ignore */ }
    }
    // Dev defaults for parent / console.
    allowed.add('127.0.0.1:5174')
    allowed.add('localhost:5174')
    allowed.add('mybo.net')
    allowed.add('www.mybo.net')
    if (!allowed.has(u.host)) return false
    const path = u.pathname || '/'
    // Production member console lives under /console — reject arbitrary mybo.net paths.
    if (u.host === 'mybo.net' || u.host === 'www.mybo.net') {
      return path === '/console' || path.startsWith('/console/')
    }
    // hq is system ops; Examples → Prompt must not bounce members there via ?next=.
    if (u.host === 'hq.mybo.net') return false
    return true
  } catch {
    return false
  }
}

function goAdmin() {
  closeMenu()
  const token = getToken()
  const nextRaw = takeLoginNext()
  if (token && nextRaw && isSafeConsoleNext(nextRaw)) {
    try {
      const u = new URL(nextRaw)
      u.searchParams.set('token', token)
      window.location.href = u.toString()
      return
    } catch { /* fall through */ }
  }
  // Members manage sites on mybo.net/console — not hq (system ops).
  const base = memberConsoleUrl.endsWith('/') ? memberConsoleUrl : `${memberConsoleUrl}/`
  const url = token ? `${base}?token=${encodeURIComponent(token)}` : base
  window.location.href = url
}

function logout() {
  closeMenu()
  setToken(null)
  me.value = null
  go('home')
}
</script>

<template>
  <nav class="site-header">
    <div class="container">
      <a href="#/" class="nav-logo" @click.prevent="go('home')">
        <img :src="logoUrl" alt="mybonet" />
      </a>
      <ul class="nav-menu">
        <li v-for="link in navLinks" :key="link.route">
          <a
            :href="link.route === 'home' ? '#/' : `#/${link.route}`"
            :class="{ active: route === link.route }"
            @click.prevent="go(link.route)"
          >{{ t(link.key) }}</a>
        </li>
      </ul>
      <div class="nav-actions">
        <LocalePicker
          class="nav-desktop-only"
          :locale="i18n.locale.value"
          :options="i18n.available.value"
          :label="t('shell.lang')"
          @change="i18n.setLocale"
        />
        <template v-if="me">
          <span class="nav-user-email nav-desktop-only">{{ me.displayName || me.email }}</span>
          <button class="nav-cta nav-desktop-only" type="button" @click="goAdmin">{{ t('nav.manageSites') }}</button>
          <a class="nav-login nav-desktop-only" href="#" @click.prevent="logout">{{ t('auth.logout') }}</a>
        </template>
        <template v-else>
          <a class="nav-login nav-desktop-only" href="#/login" @click.prevent="go('login')">{{ t('nav.auth') }}</a>
          <button class="nav-cta nav-desktop-only" type="button" @click="go('register')">{{ t('nav.ctaFree') }}</button>
        </template>
        <button
          class="nav-menu-toggle"
          type="button"
          :aria-expanded="menuOpen"
          :aria-label="menuOpen ? t('nav.closeMenu') : t('nav.openMenu')"
          @click="toggleMenu"
        >
          <svg v-if="!menuOpen" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
            <line x1="4" y1="7" x2="20" y2="7" />
            <line x1="4" y1="12" x2="20" y2="12" />
            <line x1="4" y1="17" x2="20" y2="17" />
          </svg>
          <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  </nav>

  <button
    class="nav-drawer-backdrop"
    type="button"
    :class="{ open: menuOpen }"
    :aria-label="t('nav.closeMenu')"
    @click="closeMenu"
  />
  <aside class="nav-drawer" :class="{ open: menuOpen }" :aria-hidden="!menuOpen">
    <div class="nav-drawer-head">
      <div class="nav-drawer-title">{{ t('nav.menu') }}</div>
      <button class="nav-drawer-close" type="button" :aria-label="t('nav.closeMenu')" @click="closeMenu">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
          <line x1="6" y1="6" x2="18" y2="18" />
          <line x1="18" y1="6" x2="6" y2="18" />
        </svg>
      </button>
    </div>
    <ul class="nav-drawer-menu">
      <li v-for="link in navLinks" :key="`drawer-${link.route}`">
        <a
          :href="link.route === 'home' ? '#/' : `#/${link.route}`"
          :class="{ active: route === link.route }"
          @click.prevent="go(link.route)"
        >{{ t(link.key) }}</a>
      </li>
    </ul>
    <div class="nav-drawer-actions">
      <template v-if="me">
        <span class="nav-user-email">{{ me.displayName || me.email }}</span>
        <button class="nav-cta" type="button" @click="goAdmin">{{ t('nav.manageSites') }}</button>
        <a class="nav-login" href="#" @click.prevent="logout">{{ t('auth.logout') }}</a>
      </template>
      <template v-else>
        <a class="nav-login" href="#/login" @click.prevent="go('login')">{{ t('nav.auth') }}</a>
        <button class="nav-cta" type="button" @click="go('register')">{{ t('nav.ctaFree') }}</button>
      </template>
      <div class="nav-drawer-locale">
        <LocalePicker
          variant="list"
          :locale="i18n.locale.value"
          :options="i18n.available.value"
          :label="t('shell.lang')"
          @change="i18n.setLocale"
        />
      </div>
    </div>
  </aside>

  <!-- AUTH PAGES -->
  <div v-if="isAuthPage" class="auth-wrap">
    <div class="auth-card">
      <div class="auth-logo">
        <a href="#/" @click.prevent="go('home')"><img :src="logoUrl" alt="mybonet" /></a>
      </div>

      <template v-if="route === 'login'">
        <div class="auth-title">{{ t('auth.welcomeBack') }}</div>
        <div class="auth-subtitle">{{ t('auth.loginSubtitle') }}</div>
        <div v-if="error" class="auth-error">{{ error }}</div>
        <form @submit.prevent="submitAuth('login')">
          <div class="auth-form-group">
            <label class="auth-label">Email</label>
            <input v-model="form.email" class="auth-input" type="email" required />
          </div>
          <div class="auth-form-group">
            <label class="auth-label">{{ t('auth.password') }}</label>
            <input v-model="form.password" class="auth-input" type="password" required minlength="6" />
          </div>
          <button class="auth-btn" type="submit" :disabled="busy">{{ t('auth.login') }}</button>
        </form>
        <div class="auth-links">
          <a class="auth-link" @click.prevent="go('forgot')">{{ t('auth.forgot') }}</a>
          <a class="auth-link" @click.prevent="go('register')">{{ t('auth.noAccount') }}</a>
        </div>
        <div class="auth-divider">{{ t('auth.orSocial') }}</div>
        <div class="social-login-group">
          <button class="social-btn" type="button" @click="oauth('google')">{{ t('auth.oauthGoogle') }}</button>
          <button class="social-btn" type="button" @click="oauth('facebook')">{{ t('auth.oauthFacebook') }}</button>
          <button class="social-btn" type="button" @click="oauth('line')">{{ t('auth.oauthLine') }}</button>
        </div>
      </template>

      <template v-else-if="route === 'register'">
        <div class="auth-title">{{ t('auth.createTitle') }}</div>
        <div class="auth-subtitle">{{ t('auth.registerSubtitle') }}</div>
        <div v-if="error" class="auth-error">{{ error }}</div>
        <div v-if="regMsg" class="contact-ok">{{ regMsg }}</div>
        <form @submit.prevent="submitAuth('register')">
          <div class="auth-form-group">
            <label class="auth-label">{{ t('auth.displayName') }}</label>
            <input v-model="form.displayName" class="auth-input" :placeholder="t('auth.displayNamePh')" />
          </div>
          <div class="auth-form-group">
            <label class="auth-label">Email</label>
            <div class="contact-email-row">
              <input v-model="form.email" class="auth-input" type="email" required autocomplete="email" />
              <button
                class="btn btn-primary"
                type="button"
                :disabled="busy || regCodeCooldown > 0"
                @click="sendRegisterCode"
              >{{ regCodeCooldown > 0 ? t('auth.resendIn').replace('{s}', String(regCodeCooldown)) : t('auth.sendCode') }}</button>
            </div>
          </div>
          <div class="auth-form-group">
            <label class="auth-label">{{ t('auth.verifyCode') }}</label>
            <input v-model="form.code" class="auth-input" type="text" inputmode="numeric" autocomplete="one-time-code" maxlength="8" required />
          </div>
          <div class="auth-form-group">
            <label class="auth-label">{{ t('auth.password') }}</label>
            <input v-model="form.password" class="auth-input" type="password" required minlength="6" autocomplete="new-password" />
          </div>
          <button class="auth-btn" type="submit" :disabled="busy">{{ t('auth.createAccount') }}</button>
        </form>
        <div class="auth-links">
          <span />
          <a class="auth-link" @click.prevent="go('login')">{{ t('auth.hasAccount') }}</a>
        </div>
      </template>

      <template v-else-if="route === 'forgot'">
        <div class="auth-title">{{ t('auth.forgotTitle') }}</div>
        <div class="auth-subtitle">{{ t('auth.forgotSubtitle') }}</div>
        <form @submit.prevent="go('verify')">
          <div class="auth-form-group">
            <label class="auth-label">Email</label>
            <input v-model="form.email" class="auth-input" type="email" required />
          </div>
          <button class="auth-btn" type="submit">{{ t('auth.sendReset') }}</button>
        </form>
        <div class="auth-links">
          <a class="auth-link" @click.prevent="go('login')">{{ t('auth.backLogin') }}</a>
        </div>
      </template>

      <template v-else>
        <div class="auth-title">{{ t('auth.verifyTitle') }}</div>
        <div class="auth-subtitle">{{ t('auth.verifySubtitle') }}</div>
        <button class="auth-btn" type="button" @click="go('login')">{{ t('auth.backLogin') }}</button>
      </template>
    </div>
  </div>

  <!-- MARKETING SHELL -->
  <div v-else class="page-pad">
    <!-- HOME -->
    <template v-if="route === 'home'">
      <section class="hero">
        <div class="container">
          <div class="hero-content">
            <div class="section-label">{{ t('hero.eyebrow') }}</div>
            <h1 class="hero-title">
              {{ t('hero.titleLine1') }}<br />
              <span class="hero-title-accent">{{ t('hero.titleLine2') }}</span>
            </h1>
            <p class="hero-desc">{{ t('hero.lead') }}</p>
            <div class="hero-buttons">
              <button class="btn btn-primary" type="button" @click="go(me ? 'pricing' : 'register')">{{ t('hero.ctaStart') }}</button>
              <button class="btn btn-secondary" type="button" @click="go('features')">{{ t('hero.ctaFeatures') }}</button>
            </div>
          </div>
        </div>
      </section>

      <section class="section section-dark">
        <div class="container">
          <div class="section-header">
            <div class="section-label">{{ t('home.coreLabel') }}</div>
            <h2 class="section-title">{{ t('home.coreTitle') }}</h2>
            <p class="section-subtitle">{{ t('home.coreSub') }}</p>
          </div>
          <div class="features-grid">
            <div class="feature-card" v-for="k in ['domain','theme','multisite','pay','i18n','security']" :key="k">
              <h3>{{ t(`feat.${k}.title`) }}</h3>
              <p>{{ t(`feat.${k}.desc`) }}</p>
            </div>
          </div>
        </div>
      </section>
    </template>

    <!-- FEATURES -->
    <section v-else-if="route === 'features'" class="section">
      <div class="container">
        <div class="section-header">
          <div class="section-label">{{ t('nav.features') }}</div>
          <h2 class="section-title">{{ t('features.pageTitle') }}</h2>
          <p class="section-subtitle">{{ t('features.pageSub') }}</p>
        </div>
        <div class="features-grid">
          <div class="feature-card" v-for="k in ['domain','theme','multisite','pay','i18n','security']" :key="k">
            <h3>{{ t(`feat.${k}.title`) }}</h3>
            <p>{{ t(`feat.${k}.desc`) }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- EXAMPLES -->
    <ThemeExamples v-else-if="route === 'examples'" :t="t" />

    <!-- PRICING -->
    <section v-else-if="route === 'pricing'" class="section">
      <div class="container">
        <div class="section-header">
          <div class="section-label">{{ t('nav.pricing') }}</div>
          <h2 class="section-title">{{ t('pricing.title') }}</h2>
          <p class="section-subtitle">{{ pricing?.note || t('pricing.loading') }}</p>
        </div>
        <p v-if="pricing?.display" class="section-subtitle">{{ pricing.display.note }}</p>
        <div class="features-grid" v-if="pricing">
          <article class="feature-card" v-for="p in pricing.plans" :key="p.key">
            <h3>{{ p.name }}</h3>
            <p>${{ p.monthlyUsd }} {{ t('pricing.perMonth') }}</p>
            <p v-if="p.monthlyDisplay && p.monthlyDisplay.currency !== 'USD'">
              ≈ {{ p.monthlyDisplay.currency }} {{ p.monthlyDisplay.amount }}
            </p>
            <p>{{ t('pricing.yearly') }} ${{ p.yearlyUsd }} · {{ p.maxChildSites }} {{ t('pricing.maxSites') }}</p>
            <ul>
              <li v-for="f in p.features" :key="f">{{ f }}</li>
            </ul>
            <button class="btn btn-primary" type="button" @click="go(me ? 'home' : 'register')">{{ t('hero.ctaStart') }}</button>
          </article>
        </div>
      </div>
    </section>

    <!-- LEGAL -->
    <section v-else-if="legalDoc" class="section">
      <div class="container legal-wrap">
        <div class="section-header">
          <div class="section-label">{{ t('footer.legal') }}</div>
          <h2 class="section-title">{{ legalDoc.title }}</h2>
          <p class="section-subtitle">{{ legalDoc.updated }}</p>
        </div>
        <article class="legal-doc">
          <p class="legal-intro">{{ legalDoc.intro }}</p>
          <section v-for="(s, i) in legalDoc.sections" :key="i" class="legal-section">
            <h3>{{ s.heading }}</h3>
            <p v-for="(p, j) in s.paragraphs" :key="j">{{ p }}</p>
          </section>
            <div class="legal-nav">
            <a href="#/privacy" :class="{ active: route === 'privacy' }" @click.prevent="go('privacy')">{{ t('footer.privacy') }}</a>
            <a href="#/terms" :class="{ active: route === 'terms' }" @click.prevent="go('terms')">{{ t('footer.terms') }}</a>
            <a href="#/deletion" :class="{ active: route === 'deletion' }" @click.prevent="go('deletion')">{{ t('footer.deletion') }}</a>
          </div>
        </article>
      </div>
    </section>

    <!-- CONTACT -->
    <section v-else-if="route === 'contact'" class="section">
      <div class="container">
        <div class="section-header">
          <div class="section-label">{{ t('nav.contact') }}</div>
          <h2 class="section-title">{{ t('contact.pageTitle') }}</h2>
          <p class="section-subtitle">{{ t('contact.pageSub') }}</p>
        </div>
        <div class="feature-card contact-card">
          <p>{{ t('contact.body') }}</p>
          <form class="contact-form" @submit.prevent="submitContact">
            <div class="auth-form-group">
              <label class="auth-label">{{ t('contact.name') }}</label>
              <input v-model="contact.name" class="auth-input" type="text" autocomplete="name" required maxlength="120" :disabled="contactSubmitBusy" />
            </div>
            <div class="auth-form-group">
              <label class="auth-label">{{ t('contact.email') }}</label>
              <div class="contact-email-row">
                <input v-model="contact.email" class="auth-input" type="email" autocomplete="email" required maxlength="254" :disabled="contactSendBusy || contactSubmitBusy" />
                <button
                  class="btn btn-primary"
                  type="button"
                  :disabled="contactSendBusy || contactSubmitBusy || codeCooldown > 0"
                  @click="sendContactCode"
                >
                  <span v-if="contactSendBusy" class="btn-spinner" aria-hidden="true" />
                  {{
                    contactSendBusy
                      ? t('contact.sending')
                      : (codeCooldown > 0 ? t('contact.resendIn').replace('{s}', String(codeCooldown)) : t('contact.sendCode'))
                  }}
                </button>
              </div>
            </div>
            <div class="auth-form-group">
              <label class="auth-label">{{ t('contact.code') }}</label>
              <input v-model="contact.code" class="auth-input" type="text" inputmode="numeric" autocomplete="one-time-code" maxlength="8" required :disabled="contactSubmitBusy" />
            </div>
            <div class="auth-form-group">
              <label class="auth-label">{{ t('contact.subject') }}</label>
              <input v-model="contact.subject" class="auth-input" type="text" maxlength="200" :disabled="contactSubmitBusy" />
            </div>
            <div class="auth-form-group">
              <label class="auth-label">{{ t('contact.message') }}</label>
              <textarea v-model="contact.message" class="auth-input contact-textarea" rows="5" required maxlength="8000" :disabled="contactSubmitBusy"></textarea>
            </div>
            <button class="btn btn-primary contact-submit" type="submit" :disabled="contactSendBusy || contactSubmitBusy">
              <span v-if="contactSubmitBusy" class="btn-spinner" aria-hidden="true" />
              {{ contactSubmitBusy ? t('contact.submitting') : t('contact.submit') }}
            </button>
          </form>
        </div>
      </div>
    </section>

    <!-- DOCS / PRODUCT -->
    <section v-else class="section">
      <div class="container">
        <div class="section-header">
          <div class="section-label">{{ t(`nav.${route === 'product' ? 'features' : route}`) }}</div>
          <h2 class="section-title">{{ t(`${route}.pageTitle`) }}</h2>
          <p class="section-subtitle">{{ t(`${route}.pageSub`) }}</p>
        </div>
        <div class="feature-card">
          <p>{{ t(`${route}.body`) }}</p>
        </div>
      </div>
    </section>

    <footer class="site-footer">
      <div class="container site-footer-inner">
        <div class="site-footer-brand">
          <strong>mybonet</strong>
          <span>mybo.net</span>
        </div>
        <div class="site-footer-links" aria-label="legal">
          <a href="#/privacy" @click.prevent="go('privacy')">{{ t('footer.privacy') }}</a>
          <a href="#/terms" @click.prevent="go('terms')">{{ t('footer.terms') }}</a>
          <a href="#/deletion" @click.prevent="go('deletion')">{{ t('footer.deletion') }}</a>
          <a href="#/contact" @click.prevent="go('contact')">{{ t('nav.contact') }}</a>
        </div>
        <p class="site-footer-copy">© {{ copyrightYear }} mybonet. {{ t('footer.rights') }}</p>
      </div>
    </footer>
  </div>

  <div class="toast-stack" aria-live="polite" aria-relevant="additions">
    <div
      v-for="item in toasts"
      :key="item.id"
      class="toast"
      :class="item.kind === 'ok' ? 'toast-ok' : 'toast-error'"
      role="status"
    >
      <span class="toast-text">{{ item.text }}</span>
      <button class="toast-close" type="button" :aria-label="t('contact.toastClose')" @click="dismissToast(item.id)">×</button>
    </div>
  </div>
</template>
